import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  forkJoin,
  map,
  of,
  take,
} from 'rxjs';
import { AccountService } from './api/account.service';
import { PortfolioService } from './api/portfolio.service';
import { BusinessesService } from './api/mysec/businesses.service';
import { InvestmentStatus } from '../enum/investment-status.enum';
import { EkycStatus } from '../enum/ekyc-status.enum';
import { OrderStatus } from '../enum/mysec/order-status.enum';
import { ContractStatus } from '../enum/mysec/contract-status.enum';
import { InvestmentModel } from '../models/investment/investment.model';
import { OrderFilter } from '../../pages/mysec/my-orders/order-filter';

/** The four "Things to do" cards on the investor dashboard; each counts once when shown. */
export interface TodoCardFlags {
  showEkycTodo: boolean;
  pendingPaymentCount: number;
  activeOrdersCount: number;
  refundInProgressCount: number;
}

/**
 * Number of to-do CARDS shown — the single definition of the "Things to do" count, used by the
 * dashboard page's header badge and by the sidebar / bottom-nav Dashboard badge (FRC-749).
 */
export function countTodoCards(flags: TodoCardFlags): number {
  return (
    (flags.showEkycTodo ? 1 : 0) +
    (flags.pendingPaymentCount > 0 ? 1 : 0) +
    (flags.activeOrdersCount > 0 ? 1 : 0) +
    (flags.refundInProgressCount > 0 ? 1 : 0)
  );
}

/**
 * FRC-749: shares the investor dashboard's "Things to do" count with the layout's navigation,
 * so the Dashboard nav item can show the badge on every page inside the investor dashboard.
 *
 * Two writers, one value:
 *  - `publish()` — the dashboard page pushes its own computed count once all of its sources have
 *    resolved. It is authoritative (it is literally what the page shows) and cancels any
 *    in-flight `refresh()`.
 *  - `refresh()` — the layout calls this (via `refreshIfStale()`, on init and after each
 *    navigation) so the badge is populated, and kept current, on pages other than /dashboard. It makes the same API
 *    calls as MyDashboardComponent and derives the count with the same rules (see the comments
 *    there — FRC-1029/1043); keep the two in step if either changes.
 *
 * `count$` is `null` until a value is known; consumers hide the badge for `null` and `0`.
 * A known count goes stale after STALE_AFTER_MS, or at once if any source failed (sources fail
 * soft to 0, so a failed fetch must not pass for a settled count); the next navigation re-fetches.
 */
@Injectable({
  providedIn: 'root',
})
export class DashboardTodoService {
  private static readonly INVESTMENT_TODO_TAKE = 100;
  /** A known count is re-fetched on the next navigation once it is this old. */
  static readonly STALE_AFTER_MS = 60_000;

  private readonly countSubject = new BehaviorSubject<number | null>(null);
  readonly count$: Observable<number | null> = this.countSubject.asObservable();

  private refreshSub?: Subscription;
  /** When the current count was set (Date.now()); null while unknown. */
  private countSetAt: number | null = null;
  /** The current count came from a refresh() in which at least one source failed. */
  private countIsPartial = false;

  constructor(
    private readonly accountService: AccountService,
    private readonly portfolioService: PortfolioService,
    private readonly businessesService: BusinessesService,
  ) {}

  /** Push the dashboard page's own count (authoritative). */
  publish(count: number): void {
    this.refreshSub?.unsubscribe();
    this.refreshSub = undefined;
    this.markCountSet(false);
    if (this.countSubject.value !== count) {
      this.countSubject.next(count);
    }
  }

  /** Forget the count (e.g. on logout) so a stale badge never shows for the next session. */
  reset(): void {
    this.refreshSub?.unsubscribe();
    this.refreshSub = undefined;
    this.countSetAt = null;
    this.countIsPartial = false;
    this.countSubject.next(null);
  }

  /**
   * `refresh()` when the count is unknown, came from a partly failed fetch, or is older than
   * STALE_AFTER_MS — unless a refresh is already in flight.
   */
  refreshIfStale(): void {
    const inFlight = !!this.refreshSub && !this.refreshSub.closed;
    if (inFlight) return;
    const stale =
      this.countSubject.value === null ||
      this.countIsPartial ||
      this.countSetAt === null ||
      Date.now() - this.countSetAt >= DashboardTodoService.STALE_AFTER_MS;
    if (stale) {
      this.refresh();
    }
  }

  /** Recompute the count from the same sources the dashboard page uses. Fail-soft per source. */
  refresh(): void {
    this.refreshSub?.unsubscribe();
    let failed = false;
    const onError = () => {
      failed = true;
    };
    this.refreshSub = forkJoin({
      investments: this.loadInvestmentTodos(onError),
      needsEkyc: this.loadNeedsEkyc(onError),
      activeOrdersCount: this.loadActiveOrdersCount(onError),
      hasWithheldShares: this.loadHasWithheldShares(onError),
    }).subscribe(({ investments, needsEkyc, activeOrdersCount, hasWithheldShares }) => {
      this.refreshSub = undefined;
      this.markCountSet(failed);
      this.countSubject.next(
        countTodoCards({
          showEkycTodo: needsEkyc || hasWithheldShares,
          pendingPaymentCount: investments.pendingPaymentCount,
          activeOrdersCount,
          refundInProgressCount: investments.refundInProgressCount,
        }),
      );
    });
  }

  private markCountSet(partial: boolean): void {
    this.countSetAt = Date.now();
    this.countIsPartial = partial;
  }

  /** Mirrors MyDashboardComponent.loadInvestmentTodos(). */
  private loadInvestmentTodos(onError: () => void): Observable<{
    pendingPaymentCount: number;
    refundInProgressCount: number;
  }> {
    return this.accountService
      .getMyInvestments(1, DashboardTodoService.INVESTMENT_TODO_TAKE, undefined, [
        InvestmentStatus.PendingPayment,
        InvestmentStatus.RefundRequested,
      ])
      .pipe(
        catchError(() => {
          onError();
          return of([] as InvestmentModel[]);
        }),
        map((investments) => {
          const rows = investments ?? [];
          return {
            pendingPaymentCount: rows.filter(
              (i) => i.investmentStatus === InvestmentStatus.PendingPayment,
            ).length,
            refundInProgressCount: rows.filter(
              (i) => i.investmentStatus === InvestmentStatus.RefundRequested,
            ).length,
          };
        }),
      );
  }

  /** Mirrors MyDashboardComponent.loadEkycTodo(). */
  private loadNeedsEkyc(onError: () => void): Observable<boolean> {
    return this.accountService.profile(false).pipe(
      take(1),
      map(
        (profile) =>
          !!profile && ![EkycStatus.Verified, EkycStatus.NotRequired].includes(profile.ekycStatus),
      ),
      catchError(() => {
        onError();
        return of(false);
      }),
    );
  }

  /** Mirrors MyDashboardComponent.loadActiveOrders() (FRC-833 filter). */
  private loadActiveOrdersCount(onError: () => void): Observable<number> {
    const orderFilter = new OrderFilter();
    orderFilter.take = 1;
    orderFilter.orderStatuses = [OrderStatus.Open, OrderStatus.PartiallyMatched];
    orderFilter.orderContractStatus = ContractStatus.Pending;

    return this.businessesService.getOrderList(orderFilter).pipe(
      map((res) => res?.total ?? 0),
      catchError(() => {
        onError();
        return of(0);
      }),
    );
  }

  /**
   * Mirrors MyDashboardComponent.hasWithheldShares (FRC-1029/FRC-1043): provisional, with shares
   * linked by email but not yet claimed into an ECF account.
   */
  private loadHasWithheldShares(onError: () => void): Observable<boolean> {
    return this.portfolioService.getPortfolioGl(1, 20).pipe(
      map((data) => !!data?.summary?.provisional && !!data?.summary?.hasUnclaimedShares),
      catchError(() => {
        onError();
        return of(false);
      }),
    );
  }
}
